import { Component, OnInit, Input } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { catchError, debounceTime, Subscription, throwError } from 'rxjs';
import { SyncService } from 'src/app/services/sync-service';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { formatDate } from '@angular/common';
import { CropBioKMaxData } from 'src/app/models/crop-bio-k-max-data';
import { seasonFromLabel } from '../macronutrients/season-period';

type CropBioProductKey = 'cropBio' | 'kMax' | 'rubyGlo';

/** One bar per product. K-Max and RubyGlo doses are about 10x CropBioLife's, so they share the right axis. */
interface CropBioProduct {
  key: CropBioProductKey;
  category: string;
  yAxisIndex: number;
  colors: string[];
}

@Component({
  selector: 'app-crop-bio',
  templateUrl: './crop-bio.component.html',
  styleUrls: ['./crop-bio.component.css'],
  standalone: false
})
export class CropBioComponent implements OnInit {
  @Input() fieldId: string = '';
  @Input() farmId: string;
  @Input() fromDate: String;
  @Input() toDate: String;

  chartOption: EChartsOption = {};
  hasData: boolean = true;
  isLoading: boolean = true;

  cropBioKMaxData: CropBioKMaxData = {
    cultivars: [],
    legends: [],
    cropBio: [],
    kMax: [],
    rubyGlo: []
  };

  private subscription: Subscription;
  public series: any[] = [];

  color = ['#FFB400', '#6355C7', '#5070dd', '#b6d634', '#505372', '#ff994d', '#0ca8df'];
  cropBioColors = ['#1b5e20', '#2e7d32', '#388e3c', '#43a047', '#4caf50', '#66bb6a', '#81c784'];
  kMaxColors = ['#b71c1c', '#c62828', '#d32f2f', '#e53935', '#f44336', '#ef5350', '#e57373'];
  // Purple keeps RubyGlo apart from K-Max red on the shared right axis.
  rubyGloColors = ['#4a148c', '#6a1b9a', '#7b1fa2', '#8e24aa', '#9c27b0', '#ab47bc', '#ba68c8'];

  private readonly products: CropBioProduct[] = [
    { key: 'cropBio', category: 'CropBioLife', yAxisIndex: 0, colors: this.cropBioColors },
    { key: 'kMax', category: 'K-Max', yAxisIndex: 1, colors: this.kMaxColors },
    { key: 'rubyGlo', category: 'RubyGlo', yAxisIndex: 1, colors: this.rubyGloColors }
  ];

  constructor(
    private syncService: SyncService,
    private dataService: FieldDashboardDataService
  ) {}

  ngOnInit(): void {
    this.subscription = this.dataService.fieldData$.pipe(debounceTime(100)).subscribe(data => {
      if (data.fieldId && data.farmId && data.startDate && data.endDate) {
        let fromDate: string;
        let toDate: string;

        if (data.userChangedDates) {
          fromDate = formatDate(data.startDate, 'yyyy-MM-dd', 'en-ZA');
          toDate = formatDate(data.endDate, 'yyyy-MM-dd', 'en-ZA');
        } else {
          // The season dropdown's label, e.g. "2026-2027", is 2026-06-01 to 2027-05-31.
          const season = seasonFromLabel(data.season);
          if (!season) {
            return;
          }
          fromDate = season.from;
          toDate = season.to;
        }

        if (this.fieldId === data.fieldId && this.fromDate === fromDate && this.toDate === toDate) return;

        this.fieldId = data.fieldId;
        this.farmId = '2475';
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.populateData();
      }
    });
  }

  populateData() {
    this.isLoading = true;
    this.hasData = true;
    this.syncService.getCropBioKMaxStats(this.fieldId, this.fromDate, this.toDate).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.cropBioKMaxData = response;
      this.isLoading = false;
      this.loadChartData();
    });
  }

  loadChartData(): void {
    // A missing series (rubyGlo from the PG and AllFarm sync-services) counts as zeros.
    const values = (key: CropBioProductKey): number[] => this.cropBioKMaxData?.[key] ?? [];
    this.hasData = (this.cropBioKMaxData?.cultivars?.length ?? 0) > 0 &&
      this.products.some(product => values(product.key).some(v => v !== 0));
    if (!this.hasData) return;

    // Each product's shade moves on only in the weeks it was applied.
    const shade = this.products.map(() => 0);
    this.series = [];
    this.cropBioKMaxData.legends.forEach((legend, i) => {
      // Only products applied that week get a series, so a week with none is dropped, and the week's
      // first series, which ECharts takes the legend colour from, is the first product applied.
      this.products.forEach((product, p) => {
        const value = values(product.key)[i] ?? 0;
        if (value === 0) return;
        this.series.push({
          name: 'Week ' + legend,
          type: 'bar' as const,
          stack: product.key,
          yAxisIndex: product.yAxisIndex,
          data: this.products.map((_, c) => c === p ? value : 0),
          label: { show: true, position: 'inside' as const, formatter: (l) => l.value !== 0 ? l.value.toString() : '' },
          itemStyle: { color: product.colors[shade[p]++ % product.colors.length] },
          barWidth: '40%',
          barGap: '-100%'
        });
      });
    });

    this.chartOption = {
      title: {
        text: 'CropBio, K-Max & RubyGlo/Block',
        left: 0,
        top: 10,
        textStyle: { fontSize: 18, fontWeight: 'bold' }
      },
      tooltip: {
        trigger: 'item',
        // Names the product as well as the week: K-Max and RubyGlo share the right axis.
        formatter: (params: any) => params.value !== 0 ? `${params.name}, ${params.seriesName}: ${params.value} L/Ha` : ''
      },
      legend: {
        bottom: 0,
        itemGap: 25,
        type: 'scroll'
      },
      grid: {
        left: '10%',
        right: '10%',
        bottom: '10%',
        top: '20%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: this.products.map(product => product.category)
      },
      yAxis: [
        {
          type: 'value',
          name: 'CropBio (L/Ha)',
          nameLocation: 'middle',
          axisLabel: { formatter: '{value}' },
          nameGap: 40,
          nameTextStyle: { fontWeight: 'bold' }
        },
        {
          type: 'value',
          name: 'K-Max / RubyGlo (L/Ha)',
          nameLocation: 'middle',
          axisLabel: { formatter: '{value}' },
          nameGap: 40,
          nameTextStyle: { fontWeight: 'bold' }
        }
      ],
      series: this.series
    };
  }
}
